/* ============================================================
   Dathent — Referrals dashboard (/app/referrals)
   Real data wired to /api/referral/me + /api/referral/me/signups + /api/referral/me/payouts.
   Source: design/pages/referrals.jsx, design/Referrals.html
   ============================================================ */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../services/api/client.js';
import { Icons as I } from '../components/pack-icons';
import { useAuth } from '../contexts/AuthContext.jsx';

const DEFAULT_GRADIENT = ['#D9CBB3', '#766858'];
const MAX_MESSAGE = 240;
const DEFAULT_POSITION = '50% 50%';

function parsePosition(pos) {
  const m = (pos || DEFAULT_POSITION).match(/(\d+(?:\.\d+)?)%\s+(\d+(?:\.\d+)?)%/);
  if (!m) return [50, 50];
  return [parseFloat(m[1]), parseFloat(m[2])];
}

function clamp(v, lo, hi) {
  return Math.max(lo, Math.min(hi, v));
}

function DraggablePhoto({ src, position, onPositionChange, size = 80 }) {
  const dragRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const onPointerDown = (e) => {
    if (e.button !== undefined && e.button !== 0 && e.pointerType !== 'touch') return;
    e.currentTarget.setPointerCapture(e.pointerId);
    const [px, py] = parsePosition(position);
    dragRef.current = { startX: e.clientX, startY: e.clientY, posX: px, posY: py };
    setIsDragging(true);
  };

  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    const scale = 100 / size;
    const nx = clamp(dragRef.current.posX - dx * scale, 0, 100);
    const ny = clamp(dragRef.current.posY - dy * scale, 0, 100);
    onPositionChange(`${Math.round(nx)}% ${Math.round(ny)}%`);
  };

  const onPointerUp = (e) => {
    if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    dragRef.current = null;
    setIsDragging(false);
  };

  return (
    <div
      className="rr-photo-preview rr-photo-draggable"
      role="presentation"
      style={{
        cursor: isDragging ? 'grabbing' : 'grab',
        touchAction: 'none',
        overflow: 'hidden',
        position: 'relative',
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      title="Drag to reposition"
    >
      <img
        src={src}
        alt=""
        draggable={false}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: position || DEFAULT_POSITION,
          userSelect: 'none',
          pointerEvents: 'none',
          display: 'block',
        }}
      />
    </div>
  );
}

function publicBaseUrl() {
  if (typeof window === 'undefined') return 'https://app.dathent.ai';
  return window.location.origin;
}

function platformHsl(seed) {
  let h = 0;
  for (let i = 0; i < (seed || '').length; i++) {
    h = (h + seed.charCodeAt(i) * 37) % 360;
  }
  return `hsl(${h}, 30%, 70%)`;
}

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return iso.slice(0, 10);
  }
}

function ReferralCardPreview({ name, studio, message, photoUrl, photoPosition, gradient }) {
  const grad = Array.isArray(gradient) && gradient.length >= 2 ? gradient : DEFAULT_GRADIENT;
  return (
    <div className="rr-preview-card">
      <div className="rr-preview-grad" />
      <div className="rr-preview-inner">
        <div className="rr-preview-eyebrow">Invited by</div>
        <div className="rr-preview-author">
          {photoUrl ? (
            <img
              className="rr-preview-avatar"
              src={photoUrl}
              alt=""
              style={{ objectPosition: photoPosition || '50% 50%' }}
            />
          ) : (
            <div
              className="rr-preview-avatar"
              style={{ background: `linear-gradient(135deg, ${grad[0]}, ${grad[1]})` }}
            >
              {(name || 'A').slice(0, 1).toUpperCase()}
            </div>
          )}
          <div>
            <div className="rr-preview-name">{name || 'Your name'}</div>
            <div className="rr-preview-studio">{studio || 'Your studio'}</div>
          </div>
        </div>
        <p className="rr-preview-msg">
          "{message || 'Your invite message will appear here.'}"
        </p>
        <div className="rr-preview-perk">
          <I.sparkles size={11} />
          <span>Friends get <b>2 months free</b></span>
        </div>
      </div>
    </div>
  );
}

function SetupWizard({ user, onCreated }) {
  const [name, setName] = useState(user?.name || (user?.email ? user.email.split('@')[0] : ''));
  const [studio, setStudio] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { setError('Display name is required'); return; }
    setBusy(true);
    setError(null);
    try {
      const result = await api.referral.upsertMyProfile({
        displayName: name.trim(),
        studio: studio.trim() || null,
        customMessage: null,
      });
      onCreated(result);
    } catch (err) {
      setError(err?.message || 'Failed to create profile');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="rr-setup card" onSubmit={submit}>
      <div>
        <h2>Start your referral program</h2>
        <p>Pick how you'd like to appear to friends you invite. You can change everything later.</p>
      </div>
      <div className="rr-field">
        <label className="rr-l">Display name</label>
        <input
          className="rr-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={80}
          placeholder="Alex Morgan"
          required
        />
      </div>
      <div className="rr-field">
        <label className="rr-l">Studio / company (optional)</label>
        <input
          className="rr-input"
          value={studio}
          onChange={(e) => setStudio(e.target.value)}
          maxLength={120}
          placeholder="Marlene Studio · Berlin"
        />
      </div>
      {error && <p className="rr-setup-error">{error}</p>}
      <button type="submit" className="btn btn-primary" disabled={busy}>
        {busy ? 'Creating…' : 'Create my referral link'}
      </button>
    </form>
  );
}

export function Referrals() {
  const { user } = useAuth?.() || {};
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState({ signups: 0, activated: 0, pendingPayoutCents: 0, earnedCents: 0, paidOutCents: 0 });
  const [signups, setSignups] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingField, setSavingField] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [filter, setFilter] = useState('all');
  const [payoutBusy, setPayoutBusy] = useState(false);

  const fileRef = useRef(null);
  const debounceRef = useRef(null);

  const [draftName, setDraftName] = useState('');
  const [draftStudio, setDraftStudio] = useState('');
  const [draftMessage, setDraftMessage] = useState('');
  const [draftPosition, setDraftPosition] = useState(DEFAULT_POSITION);
  const positionDebounceRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const me = await api.referral.getMyProfile();
        if (cancelled) return;
        if (me?.profile) {
          setProfile(me.profile);
          if (me.stats) setStats({ ...stats, ...me.stats });
          setDraftName(me.profile.displayName || '');
          setDraftStudio(me.profile.studio || '');
          setDraftMessage(me.profile.customMessage || '');
          setDraftPosition(me.profile.photoPosition || DEFAULT_POSITION);
          const [sg, py] = await Promise.all([
            api.referral.listSignups().catch(() => ({ signups: [], counts: { total: 0, activated: 0 } })),
            api.referral.listPayouts().catch(() => ({ payouts: [] })),
          ]);
          if (cancelled) return;
          setSignups(sg.signups || []);
          setPayouts(py.payouts || []);
          if (sg.counts) {
            setStats((prev) => ({ ...prev, signups: sg.counts.total, activated: sg.counts.activated }));
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const link = profile ? `${publicBaseUrl()}/r/${profile.code}` : '';

  const copyLink = useCallback(() => {
    if (!link) return;
    navigator.clipboard?.writeText(link).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  }, [link]);

  const debouncedSave = useCallback((patch) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSavingField('field');
      try {
        const result = await api.referral.upsertMyProfile(patch);
        if (result?.profile) setProfile(result.profile);
      } catch (err) {
        console.error('Failed to save referrer profile', err);
      } finally {
        setSavingField(null);
      }
    }, 600);
  }, []);

  const onNameChange = (v) => {
    setDraftName(v);
    debouncedSave({ displayName: v, regenerateCode: true });
  };
  const onStudioChange = (v) => {
    setDraftStudio(v);
    debouncedSave({ studio: v, regenerateCode: true });
  };
  const onMessageChange = (v) => {
    const next = v.slice(0, MAX_MESSAGE);
    setDraftMessage(next);
    debouncedSave({ customMessage: next });
  };
  const onPositionChange = (next) => {
    setDraftPosition(next);
    if (positionDebounceRef.current) clearTimeout(positionDebounceRef.current);
    positionDebounceRef.current = setTimeout(async () => {
      try {
        const result = await api.referral.upsertMyProfile({ photoPosition: next });
        if (result?.profile) setProfile(result.profile);
      } catch (err) {
        console.error('Failed to save photo position', err);
      }
    }, 400);
  };

  const onPhotoPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadError(null);
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('Image must be under 5 MB');
      return;
    }
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setUploadError('Only JPG, PNG, or WEBP images are supported');
      return;
    }
    try {
      const result = await api.referral.uploadPhoto(file);
      if (result?.photoUrl) {
        setProfile((p) => (p ? { ...p, photoUrl: result.photoUrl } : p));
      }
    } catch (err) {
      setUploadError(err?.message || 'Upload failed');
    }
  };

  const removePhoto = async () => {
    try {
      const result = await api.referral.upsertMyProfile({ photoUrl: null });
      if (result?.profile) setProfile(result.profile);
    } catch (err) {
      setUploadError(err?.message || 'Failed to remove photo');
    }
  };

  const filteredSignups = useMemo(() => {
    if (filter === 'all') return signups;
    return signups.filter((s) => s.status === filter);
  }, [signups, filter]);

  const requestPayout = async () => {
    if (stats.pendingPayoutCents <= 0 || payoutBusy) return;
    setPayoutBusy(true);
    try {
      await api.referral.requestPayout({ amountCents: stats.pendingPayoutCents });
      const py = await api.referral.listPayouts();
      setPayouts(py.payouts || []);
    } catch (err) {
      console.error('Payout request failed', err);
    } finally {
      setPayoutBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="rr-root">
        <div style={{ padding: '64px 0', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
          Loading…
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="rr-root">
        <SetupWizard user={user} onCreated={(r) => {
          if (r?.profile) {
            setProfile(r.profile);
            setDraftName(r.profile.displayName || '');
            setDraftStudio(r.profile.studio || '');
            setDraftMessage(r.profile.customMessage || '');
          }
        }} />
      </div>
    );
  }

  const gradient = profile.avatarGradient || DEFAULT_GRADIENT;

  return (
    <div className="rr-root">
      <div className="rr-top">
        <div>
          <Link className="rr-back" to="/app/settings"><I.chevLeft size={14} /> Settings</Link>
          <h1 className="rr-h">Referral program</h1>
          <p className="rr-sub">
            Share Dathent, earn {profile.referralRate}% of every paying friend's first year. They get 2 months free.
          </p>
        </div>
        <button
          className="btn btn-primary"
          onClick={requestPayout}
          disabled={stats.pendingPayoutCents <= 0 || payoutBusy}
        >
          <I.zap size={13} /> Request payout · €{(stats.pendingPayoutCents / 100).toFixed(0)}
        </button>
      </div>

      <div className="rr-stats">
        <div className="card rr-stat">
          <div className="rr-stat-l">Total earned</div>
          <div className="rr-stat-v">€{(stats.earnedCents / 100).toLocaleString()}</div>
          <div className="rr-stat-sub">all-time, gross</div>
        </div>
        <div className="card rr-stat">
          <div className="rr-stat-l">Pending payout</div>
          <div className="rr-stat-v">€{(stats.pendingPayoutCents / 100).toFixed(0)}</div>
          <div className="rr-stat-sub">{stats.pendingPayoutCents > 0 ? 'ready to request' : 'no balance yet'}</div>
        </div>
        <div className="card rr-stat">
          <div className="rr-stat-l">Signups</div>
          <div className="rr-stat-v">{stats.signups}</div>
          <div className="rr-stat-sub">{stats.activated} activated</div>
        </div>
        <div className="card rr-stat">
          <div className="rr-stat-l">Active referrals</div>
          <div className="rr-stat-v">{stats.activated}</div>
          <div className="rr-stat-sub">earning {profile.referralRate}% recurring</div>
        </div>
      </div>

      <div className="rr-grid">
        <div className="rr-col">
          <section className="card rr-card">
            <header className="rr-card-h">
              <div className="eyebrow"><span>Your link</span></div>
              <h2>Share this anywhere.</h2>
              <p>Anyone who signs up through it gets attributed to you for the lifetime of their account.</p>
            </header>
            <div className="rr-link">
              <span className="rr-link-prefix">{publicBaseUrl().replace(/^https?:\/\//, '')}/r/</span>
              <span className="rr-link-code">{profile.code}</span>
              <button type="button" className={`rr-copy ${copied ? 'is-copied' : ''}`} onClick={copyLink}>
                {copied ? (<><I.check size={13} /> Copied</>) : (<><I.copy size={13} /> Copy</>)}
              </button>
            </div>
            <div className="rr-share">
              <span className="rr-share-l">Quick share</span>
              <a className="rr-share-btn" href={`mailto:?subject=${encodeURIComponent('Try Dathent')}&body=${encodeURIComponent(link)}`}>
                <I.send size={12} /> Email
              </a>
              <a className="rr-share-btn" target="_blank" rel="noreferrer" href={`https://t.me/share/url?url=${encodeURIComponent(link)}`}>
                <I.send size={12} /> Telegram
              </a>
              <a className="rr-share-btn" target="_blank" rel="noreferrer" href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(link)}`}>
                <I.link size={12} /> LinkedIn
              </a>
              <a className="rr-share-btn" target="_blank" rel="noreferrer" href={`/r/${profile.code}`}>
                <I.link size={12} /> Open
              </a>
            </div>
          </section>

          <section className="card rr-card">
            <header className="rr-card-h">
              <div className="eyebrow"><span>Personalize the invite</span></div>
              <h2>How friends see your invite.</h2>
              <p>The signup page they land on shows your name, photo, and message. Personal works better than generic.</p>
            </header>
            <div className="rr-form">
              <div className="rr-field">
                <label className="rr-l">Profile photo</label>
                <div className="rr-photo">
                  {profile.photoUrl ? (
                    <DraggablePhoto
                      src={profile.photoUrl}
                      position={draftPosition}
                      onPositionChange={onPositionChange}
                      size={80}
                    />
                  ) : (
                    <div
                      className="rr-photo-preview"
                      style={{ background: `linear-gradient(135deg, ${gradient[0]}, ${gradient[1]})` }}
                    >
                      {(draftName || 'A').slice(0, 1).toUpperCase()}
                    </div>
                  )}
                  <div className="rr-photo-actions">
                    <div>
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => fileRef.current?.click()}>
                        <I.upload size={12} /> {profile.photoUrl ? 'Replace' : 'Upload photo'}
                      </button>
                      {profile.photoUrl && (
                        <button type="button" className="btn btn-ghost btn-sm" onClick={removePhoto}>
                          <I.trash size={12} /> Remove
                        </button>
                      )}
                    </div>
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      hidden
                      onChange={onPhotoPick}
                    />
                    <div className="rr-photo-hint">
                      JPG, PNG, or WEBP. Max 5 MB. {profile.photoUrl ? 'Drag the photo to reposition.' : ''}
                    </div>
                    {uploadError && <div className="rr-photo-error">{uploadError}</div>}
                  </div>
                </div>
              </div>

              <div className="rr-field">
                <label className="rr-l">Display name</label>
                <input
                  className="rr-input"
                  value={draftName}
                  onChange={(e) => onNameChange(e.target.value)}
                  maxLength={80}
                />
              </div>

              <div className="rr-field">
                <label className="rr-l">Studio / company</label>
                <input
                  className="rr-input"
                  value={draftStudio}
                  onChange={(e) => onStudioChange(e.target.value)}
                  maxLength={120}
                />
              </div>

              <div className="rr-field">
                <div className="rr-l-row">
                  <label className="rr-l">Invite message</label>
                  <span className="rr-l-count">{draftMessage.length} / {MAX_MESSAGE}</span>
                </div>
                <textarea
                  className="rr-input rr-textarea"
                  rows={3}
                  maxLength={MAX_MESSAGE}
                  value={draftMessage}
                  onChange={(e) => onMessageChange(e.target.value)}
                />
              </div>
              {savingField && (
                <div style={{ fontSize: 11, color: 'var(--text-subtle)' }}>Saving…</div>
              )}
            </div>
          </section>

          <section className="card rr-card">
            <header className="rr-card-h rr-card-h-row">
              <div>
                <div className="eyebrow"><span>Referrals</span></div>
                <h2>People who signed up.</h2>
              </div>
              <div className="rr-tabs">
                {[
                  { id: 'all', label: 'All' },
                  { id: 'waitlist', label: 'Waitlist' },
                  { id: 'activated', label: 'Activated' },
                ].map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    className={`rr-tab ${filter === t.id ? 'is-active' : ''}`}
                    onClick={() => setFilter(t.id)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </header>

            <div className="rr-table">
              <div className="rr-table-h">
                <span>Person</span>
                <span>Status</span>
                <span>Joined</span>
                <span style={{ textAlign: 'right' }}>Contact</span>
              </div>
              {filteredSignups.length === 0 && (
                <div className="rr-table-empty">No signups yet. Share your link to get started.</div>
              )}
              {filteredSignups.map((r) => (
                <div key={r.id} className="rr-table-row">
                  <div className="rr-person">
                    <div className="rr-person-av" style={{ background: platformHsl(r.email) }}>
                      {(r.email || '?').slice(0, 1).toUpperCase()}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div className="rr-person-name">{r.email}</div>
                      <div className="rr-person-sub">{r.locale?.toUpperCase() || 'EN'}</div>
                    </div>
                  </div>
                  <span className={`pill rr-pill-${r.status}`}>
                    <span className="dot" />
                    {r.status === 'activated' ? 'Activated' : r.status === 'churned' ? 'Churned' : 'Waitlist'}
                  </span>
                  <span className="rr-cell-meta">{formatDate(r.createdAt)}</span>
                  <span className="rr-cell-contact">
                    {r.contactValue || '—'}
                    {r.contactKind && r.contactValue && <span>{r.contactKind}</span>}
                  </span>
                </div>
              ))}
            </div>
          </section>
        </div>

        <aside className="rr-side">
          <div className="rr-side-sticky">
            <div className="rr-side-h">
              <div className="eyebrow"><span>Live preview</span></div>
              <h3>Your signup page</h3>
              <p>
                Updates as you edit.{' '}
                <a href={`/r/${profile.code}`} target="_blank" rel="noreferrer">Open full preview ↗</a>
              </p>
            </div>
            <ReferralCardPreview
              name={draftName}
              studio={draftStudio}
              message={draftMessage}
              photoUrl={profile.photoUrl}
              photoPosition={draftPosition}
              gradient={gradient}
            />

            <section className="card rr-payouts">
              <header className="rr-card-h">
                <div className="eyebrow"><span>Payouts</span></div>
                <h3>Recent</h3>
              </header>
              <div className="rr-payout-list">
                {payouts.length === 0 && (
                  <div className="rr-payouts-empty">No payouts yet.</div>
                )}
                {payouts.map((p) => (
                  <div key={p.id} className="rr-payout">
                    <div className="rr-payout-l">
                      <div className="rr-payout-amount">€{(p.amountCents / 100).toFixed(0)}</div>
                      <div className="rr-payout-method">{p.method || 'pending method'}</div>
                    </div>
                    <div className="rr-payout-r">
                      <span className="rr-payout-when">{formatDate(p.requestedAt)}</span>
                      <span className={`pill ${p.status === 'paid' ? 'pill-success' : ''}`} style={{ height: 18 }}>
                        {p.status === 'paid' ? <I.check size={9} /> : null}
                        {' '}{p.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </div>
        </aside>
      </div>
    </div>
  );
}

export default Referrals;
